import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Burger, Container, Drawer, Group, Stack } from '@mantine/core';
import Logo from '@/components/Logo';
import touchTarget from '@/components/common/touchTarget.module.css';
import classes from './header.module.css';

/** Класс пункта меню в шапке: выбранный выделен весом и меткой на линии шапки. */
export function navLinkClass(active: boolean): string {
  return active ? `${classes.link} ${classes.linkActive}` : `${classes.link}`;
}

/** Класс строки мобильного меню: выбранная строка выделена подложкой. */
export function drawerItemClass(active = false): string {
  return active ? `${classes.drawerItem} ${classes.drawerItemActive}` : `${classes.drawerItem}`;
}

/** Класс самой шапки для `AppShell.Header`: белый фон и зелёная линия снизу. */
export const headerClass = `${classes.header}`;

interface HeaderShellProps {
  /** Куда ведёт логотип. */
  readonly logoTo?: string;
  /** Пункты основного меню для широкого экрана. */
  readonly nav: ReactNode;
  /** Правый край шапки: виден на всех ширинах, сам решает, что скрыть на телефоне. */
  readonly actions: ReactNode;
  readonly menuOpened: boolean;
  readonly onMenuToggle: () => void;
  readonly onMenuClose: () => void;
  /** Заголовок мобильного меню; по умолчанию «Меню». */
  readonly menuTitle?: ReactNode;
  /** Содержимое мобильного меню. */
  readonly menu: ReactNode;
}

// Общая оболочка шапки до входа и в кабинете: одна разметка, ширина содержимого страницы, одно мобильное меню.
// Что в шапке — решают PublicHeader и AppHeader; здесь только раскладка и оформление.
export default function HeaderShell({
  logoTo,
  nav,
  actions,
  menuOpened,
  onMenuToggle,
  onMenuClose,
  menuTitle,
  menu,
}: HeaderShellProps) {
  const { t } = useTranslation();

  return (
    <Container size="lg" h="100%">
      <Group h="100%" gap={32} wrap="nowrap">
        {/* Надпись в SVG сидит в нижней половине: сдвиг ставит «DATA1861» на одну линию с пунктами меню. */}
        <Logo to={logoTo} height={24} offsetY={-2} />
        <Box component="nav" aria-label={t('nav.mainLabel')} className={classes.nav} visibleFrom="md">
          {nav}
        </Box>
        <Group h="100%" gap="sm" ml="auto" wrap="nowrap">
          {actions}
          <Burger
            opened={menuOpened}
            onClick={onMenuToggle}
            hiddenFrom="md"
            size="sm"
            className={touchTarget.target}
            aria-label={t('nav.menu')}
            aria-expanded={menuOpened}
          />
        </Group>
      </Group>

      <Drawer
        opened={menuOpened}
        onClose={onMenuClose}
        position="right"
        size="xs"
        title={menuTitle ?? t('nav.menu')}
        hiddenFrom="md"
        closeButtonProps={{ 'aria-label': t('nav.closeMenu'), className: touchTarget.target }}
      >
        <Stack gap={4}>{menu}</Stack>
      </Drawer>
    </Container>
  );
}
